import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, Grid, Paper, Stack, Typography } from "@mui/material";
import VideoCall from "./VideoConsultationComponents/VideoCall";
import AppointmentChat from "./VideoConsultationComponents/AppointmentChat";
import "./joinVideoConsultation.css";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import momentTz from "moment-timezone";
import ChatComponent from "./ChatComponent/ChatComponent";
import { AccessTime, AccountCircle, ArrowCircleLeftOutlined, AttachFile, CakeOutlined, CalendarToday, CallEndRounded, CardMembershipOutlined, FilePresentOutlined, FilePresentRounded, FileUploadRounded, LocalHospital, LockClockOutlined, MedicalServicesOutlined, PersonOutline, Schedule, Timer, TimerOff, TimerOutlined, ToggleOffOutlined, ToggleOnOutlined } from "@mui/icons-material";
import ReviewCard from "components/common/ReviewModal/ReviewCard";
import axios from "axios";
import { getToken } from "auth/authenticate";
import DeedButtons from "components/common/Elements/DeedButtons";
import useAlert from "./VideoConsultationComponents/useAlert";

const JoinVideoConsultation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const [activeView, setActiveView] = useState("idle");
  const [activeChatView, setActiveChatView] = useState("idle");
  const [isJoinDisabled, setIsJoinDisabled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const secretData = location.state?.secretData;
  const { showAlert, AlertComponent } = useAlert();
  const [endConsultationClicked, setEndConsultationClicked] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const chatRef = React.useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachments, setAttachments] = useState(secretData?.appointmentData?.attachments || []);


  useEffect(() => {
    if (!secretData?.appointmentData?.appointmentId) {
      console.warn(
        "Order ID or Appointment ID is missing. Restricting actions."
      );
      setIsJoinDisabled(true);
    }
  }, [secretData]);

  useEffect(() => {
    const slotTime = secretData?.appointmentData?.slotTime;
    if (slotTime) {
      setIsJoinDisabled(!isJoinAllowed(slotTime));
    }
  }, [secretData]);

  // Use useEffect to reset the state after it's consumed
  useEffect(() => {
    if (endConsultationClicked) {
      setEndConsultationClicked(false);
    }
  }, [endConsultationClicked]);


  useEffect(() => {
    if (secretData?.appointmentData?.appointmentId) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}appointment-review/${!secretData.appointmentData.appointmentId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      })
        .then(res => {
          if (res?.data?.data?.patient?.reviewId) {
            setHasReviewed(true);
            // alert('Already reviewed. review Id : ' + JSON.stringify(res?.data?.data?.patient?.reviewId));
          } else {
            setHasReviewed(false);
            // alert('Not reviewed yet : ' +  JSON.stringify(res?.data?.data?.patient?.reviewId))
          }

        })
        .catch(() => setHasReviewed(false));
    }
  }, [secretData?.appointmentData]);

  const handleViewChange = (view) => {
    setActiveView(view);
    setIsExpanded(view === "join");
  };

  const handleToggleChatView = () => {
    // if (activeChatView == "chat") {
    //   setActiveChatView("idle");
    // } else {
    //   setActiveChatView("chat");
    // }

    setActiveChatView((prev) => (prev === "chat" ? "idle" : "chat"));
    setTimeout(() => {
      if (chatRef.current && activeChatView !== "chat") {
        chatRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100); // Delay to ensure chat is rendered
  };

  const handleTime = (isoUtcTimeString1) => {
    try {
      if (!momentTz(isoUtcTimeString1, momentTz.ISO_8601, true).isValid()) {
        return "Invalid Time";
      }
      // Get the user's time zone
      const userTimeZone = secretData?.userData?.User?.timezone;

      // Parse the ISO UTC time and convert it to the user's time zone
      const userLocalTime = momentTz.utc(isoUtcTimeString1).tz(userTimeZone);

      return userLocalTime.format("hh:mm A"); // Format as desired
    } catch (error) {
      console.error("Error converting time:", error);
      return null;
    }
  };

  const handleExpiryTime = (isoUtcTimeString2) => {
    try {
      if (!momentTz(isoUtcTimeString2, momentTz.ISO_8601, true).isValid()) {
        return "Invalid Time";
      }
      // Get the user's time zone
      const userTimeZone = secretData?.userData?.User?.timezone;

      // Parse the ISO UTC time, add 1 hour, and convert it to the user's time zone
      const expiryTime = momentTz
        .utc(isoUtcTimeString2)
        .add(1, "hour")
        .tz(userTimeZone);

      return expiryTime.format("YYYY-MM-DD hh:mm A"); // Format as desired
    } catch (error) {
      console.error("Error handling expiry time:", error);
      return null;
    }
  };

  const isAppointmentExpired = (isoUtcTimeString) => {
    try {
      // Ensure the input time is valid
      if (!momentTz(isoUtcTimeString, momentTz.ISO_8601, true).isValid()) {
        return false; // Invalid time means we can't determine expiry
      }

      // Get the user's time zone
      const userTimeZone = secretData?.userData?.User?.timezone;

      // Calculate the expiry time
      const expiryTime = momentTz
        .utc(isoUtcTimeString)
        .add(1, "hour")
        .tz(userTimeZone);

      // Get the current time in the user's time zone
      const currentTime = momentTz().tz(userTimeZone);

      // Compare expiry time with the current time
      return expiryTime.isBefore(currentTime);
    } catch (error) {
      console.error("Error determining if appointment has expired:", error);
      return false; // Default to false in case of an error
    }
  };

  const isJoinAllowed = (isoUtcTimeString) => {
    try {
      if (!momentTz(isoUtcTimeString, momentTz.ISO_8601, true).isValid()) {
        return false;
      }
      const userTimeZone = secretData?.userData?.User?.timezone;
      const slotTime = momentTz.utc(isoUtcTimeString).tz(userTimeZone);
      const currentTime = momentTz().tz(userTimeZone);

      // Time boundaries
      const oneHourBefore = slotTime.clone().subtract(1, "hour");
      const oneHourAfter = slotTime.clone().add(1, "hour");

      // Check if current time is within the allowed window
      return currentTime.isBetween(oneHourBefore, oneHourAfter, null, "[]");
    } catch (error) {
      console.error("Error in isJoinAllowed:", error);
      return false;
    }
  };

  const calculateAge = (birthDateString) => {
    const birthDate = new Date(birthDateString);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();

    // Adjust if the current month is before the birth month
    if (months < 0) {
      years--;
      months += 12;
    }

    // Adjust if it's the birth month but the current day is before the birth day
    if (months === 0 && today.getDate() < birthDate.getDate()) {
      years--;
      months = 11;
    }

    return `${years} Years, ${months} Months`;
  };

  const getAttachmentNameFromUrl = (url) => {
    let last = url.substring(url.lastIndexOf("/") + 1, url.length);
    let splitted = last.split("?AWSAccessKeyId=");
    return splitted[0];
  };


  const handleEndVideoCall = () => {
    if (!hasReviewed) {
      setShowReviewModal(true);
    }
    setIsJoinDisabled(true);

    setActiveView("idle");
    setIsExpanded(false);
  };

  const handleReviewSent = () => {
    setShowReviewModal(false);
    window.location.reload(); // force reload after review
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files.slice(0, 1));
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClickOnConsultationComplete = () => {
    setEndConsultationClicked(true);
  };

  // Update handleUploadFiles
  const handleUploadFiles = async () => {
    if (!selectedFiles.length) {
      showAlert("warning", "alert", "Please select a file to upload");
      return;
    }



    const formData = new FormData();
    formData.append("file", selectedFiles[0]);
    formData.append("appointmentId", secretData.appointmentData.appointmentId);
    formData.append("fileType", "Patient Record");

    try {
      const result = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}appointment/patient/upload-attachment`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (result?.status === 201) {
        // Update attachments state with new data
        if (result?.data?.data?.attachments) {
          setAttachments(result.data.data.attachments);
          setSelectedFiles([]);
          showAlert("success", "alert", "File uploaded successfully");
        }
      }
    } catch (error) {
      console.error("File upload error:", error);
      showAlert(
        "error",
        "alert",
        error.response?.data?.message || "Failed to upload file"
      );
    }
  };

  const categorizeAttachments = (attachments = []) => {
    const patientsReports = [];
    const others = [];
    attachments.forEach((url) => {
      const name = getAttachmentNameFromUrl(url);
      if (name.startsWith("Patient_Record")) {
        patientsReports.push(url);
      } else {
        others.push(url);
      }
    });
    return { patientsReports, others };
  };

  const { patientsReports, others } = useMemo(() =>
    categorizeAttachments(attachments),
    [attachments]
  );

  const appointmentDetailsStyle = {
    width: isExpanded ? "95%" : "500px",
    height: isExpanded ? "40px" : "fit-content",
    margin: "0 auto",
    borderRadius: "8px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#fff",
    // border: "1px solid #1a73e8",
    transition: "width 0.5s ease-out, height 0.5s ease-out",
    display: isExpanded ? "flex" : "flex",
    flexDirection: isExpanded ? "row" : "column",
    justifyContent: isExpanded ? "flex-start" : "center",
    alignItems: "center",
  };

  return (
    <div className="join-video-consultation">
      {secretData?.appointmentData?.status == "COMPLETED" ? (
        <h2 className="heading">Consultation Completed</h2>
      ) : (
        <h2 className="heading">Join Video Consultation</h2>
      )}
      <Button onClick={() => navigate(-1)}>
        <ArrowCircleLeftOutlined />{" "}
        <span style={{ marginLeft: "5px" }}>Go Back</span>
      </Button>

      {AlertComponent}

      <Box className="appointment-box">

        <Dialog
          open={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, p: 1 }
          }}
        >
          <ReviewCard
            appointment={secretData?.appointmentData}
            onReviewSent={handleReviewSent}
          />
        </Dialog>

        <Paper
          sx={appointmentDetailsStyle}
          elevation={3}
          className="appointment-details"
        >
          {/* <pre style={{width:'400px', textWrap:'wrap'}}>{JSON.stringify(secretData)}</pre> */}

          {isExpanded ? (
            <>
              <Box
                className="appointment-details-collap"
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: { xs: "flex-start", md: "space-between" },
                  gap: { xs: 2, md: 0 },
                  px: { xs: 2, md: 3 },
                  py: { xs: 2, md: 1 }
                }}
              >
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#e3f2fd',
                  px: { xs: '0rem', sm: '0.025rem', md: '1rem' },
                  py: 0.5,
                  borderRadius: 2,
                  border: '1px solid #90caf9',
                  width: { xs: '70%', sm: "40%", md: 'auto' }
                }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      color: '#1976d2',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      padding: '2px',
                      fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                    }}
                  >
                    <Timer fontSize="small" />
                    Consultation in Progress
                  </Typography>
                </Box>

                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: { xs: 2, md: 4 },
                  width: { xs: '100%', md: 'auto' }
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <TimerOutlined color="primary" />
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Start Time
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {handleTime(secretData?.appointmentData?.slotTime) || "Invalid Time"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderLeft: { xs: 'none', md: '1px solid #e0e0e0' },
                    pl: { xs: 0, md: 3 },
                    width: { xs: '100%', md: 'auto' },
                    pt: { xs: 1, md: 0 },
                    borderTop: { xs: '1px solid #e0e0e0', md: 'none' }
                  }}>
                    <MedicalServicesOutlined color="success" />
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Doctor
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {secretData?.appointmentData?.Doctor?.name || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Reg.No: {secretData?.appointmentData?.Doctor?.regNo || "N/A"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <LockClockOutlined color="warning" />
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Expire
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {handleExpiryTime(secretData?.appointmentData?.slotTime) || "Invalid Time"}
                      </Typography>
                    </Box>
                  </Box>


                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{
                  color: '#1976d2',
                  borderBottom: '2px solid #1976d2',
                  pb: 1,
                  mb: 2
                }}>
                  Appointment Details
                </Typography>
                <Typography variant="body2" sx={{
                  color: '#1976d2',
                  mb: 3
                }}>
                  ID : {secretData?.appointmentData?.appointmentId}
                </Typography>

                

                <Grid container spacing={3}>
                  {/* Patient Section */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      bgcolor: '#f5f5f5',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="subtitle1" sx={{
                        color: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}>
                        <AccountCircle /> Patient Information
                      </Typography>

                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonOutline color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">Name</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {secretData?.appointmentData?.Patient?.name || "Unknown Patient"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CakeOutlined color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">Age</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {calculateAge(secretData?.appointmentData?.Patient?.dob) || "Unknown"}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>

                  {/* Doctor Section */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{
                      bgcolor: '#f5f5f5',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="subtitle1" sx={{
                        color: '#2196f3',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}>
                        <LocalHospital /> { secretData?.appointmentData?.Doctor?.professionType || "Health Professional"} Information
                      </Typography>

                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MedicalServicesOutlined color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">Name</Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {secretData?.appointmentData?.Doctor?.name || "Provider"}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CardMembershipOutlined color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">{ secretData?.appointmentData?.Doctor?.regNoAuthority || "Reg. No."} : </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {secretData?.appointmentData?.Doctor?.regNo || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>

                  {/* Appointment Time Section */}
                  <Grid item xs={12}>
                    <Box sx={{
                      bgcolor: '#e3f2fd',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #90caf9'
                    }}>
                      <Typography variant="subtitle1" sx={{
                        color: '#1976d2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}>
                        <Schedule /> Appointment Schedule
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <CalendarToday color="primary" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">Date</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {secretData?.appointmentData?.date || "Invalid Date"}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <AccessTime color="primary" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">Start Time</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {handleTime(secretData?.appointmentData?.slotTime)} ({secretData?.userData?.User?.timezone})
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <TimerOff color="error" fontSize="small" />
                            <Box>
                              <Typography variant="caption" color="textSecondary">Expires At</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {handleExpiryTime(secretData?.appointmentData?.slotTime)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Attachments Section */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        bgcolor: "#fff",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <FilePresentRounded></FilePresentRounded> Attachments
                        </Typography>

                        {patientsReports?.length > 0 ? (
                          <Stack spacing={1}>
                            {patientsReports.map((url) => (
                              <Button
                                key={url}
                                variant="outlined"
                                startIcon={<FilePresentOutlined />}
                                href={url}
                                sx={{
                                  justifyContent: "flex-start",
                                  textTransform: "none",
                                }}
                                target="_blank"
                              >
                                {getAttachmentNameFromUrl(url)}
                              </Button>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No attachments available
                          </Typography>
                        )}
                      </Box>
                      <Box p={2}>
                        <Button
                          variant="contained"
                          component="label"
                          style={{
                            color: "white",
                            background: "#3C3D37",
                            fontSize: "12px",
                            transition: "background 0.2s, color 0.2s",
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = "#23241e";
                            e.currentTarget.style.color = "#8DD8FF";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = "#3C3D37";
                            e.currentTarget.style.color = "white";
                          }}
                        >
                          <FileUploadRounded />
                          Upload
                          <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>
                        <Stack spacing={1} mt={1}>
                          {selectedFiles.map((file, idx) => (
                            <Box
                              key={idx}
                              display="flex"
                              alignItems="center"
                              gap={1}
                            >
                              <FilePresentOutlined fontSize="small" />
                              <Typography variant="body2">
                                {file.name}
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFile(idx)}
                              >
                                Remove
                              </Button>
                            </Box>
                          ))}
                        </Stack>
                        {selectedFiles.length > 0 && (
                          <DeedButtons
                            variant="contained"
                            color="primary"
                            sx={{ mt: 1, p: 1.2, fontSize: '0.95rem' }}
                            onClick={handleUploadFiles}
                          >
                            Submit Attachments
                          </DeedButtons>
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Status Messages */}
                  {isAppointmentExpired(secretData?.appointmentData?.slotTime) ==
                    false && isJoinDisabled ? (
                    <p style={{ color: "plum", marginTop: "10px" }}>
                      You can join one hour before appointment time.
                    </p>
                  ) : (
                    ""
                  )}

                  {isAppointmentExpired(secretData?.appointmentData?.slotTime) ==
                    true && (
                      <p style={{ color: "plum", marginTop: "10px" }}>
                        Appointment is expired.
                      </p>
                    )}
                </Grid>
              </Box>
            </>
          )}
          <div
            className="button-group"
            style={
              isExpanded
                ? {
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignContent: "space-between",
                  width: "20%",
                }
                : {
                  display: "flex",
                  justifyContent: "space-evenly",
                  width: "90%",
                  marginTop: "15px",
                }
            }
          >
            {!secretData?.appointmentData?.appointmentId ? (
              ""
            ) : (
              <>
                {isAppointmentExpired(secretData?.appointmentData?.slotTime) ==
                  true ? (
                  ""
                ) : (
                  //  <Button
                  //                 style={
                  //                   isExpanded
                  //                     ? { margin: "0px 5px", width: "50%" }
                  //                     : { margin: "0px 5px" }
                  //                 }
                  //                 variant="contained"
                  //                 color="primary"
                  //                 onClick={() => handleViewChange("join")}
                  //                 disabled={activeView === "join"}
                  //                 className="toggle-button"
                  //               >
                  //                 Join Appointment
                  //               </Button>

                  <>
                    {/* <Button
                    style={
                      isExpanded
                        ? { margin: "0px 5px", width: "50%" }
                        : { margin: "0px 5px" }
                    }
                    variant="contained"
                    color="primary"
                    onClick={() => { handleViewChange("join"); }}
                    disabled={isJoinDisabled || activeView === "join" || secretData?.appointmentData?.status == "COMPLETED"}
                    className="toggle-button"
                  >
                    Join Appointment
                  </Button> */}

                    <DeedButtons
                      style={
                        isExpanded
                          ? { margin: "0px 5px", width: "50%" }
                          : { margin: "0px 5px" }
                      }
                      // type="submit"
                      variant="contained"
                      onClick={() => {
                        handleViewChange("join");
                      }}
                      disabled={
                        isJoinDisabled ||
                        activeView === "join" ||
                        secretData?.appointmentData?.status == "COMPLETED"
                      }
                      sx={{
                        mt: { xs: 1.5, sm: 2 },
                        padding: "7px 15px !important",
                        height: { xs: 40, sm: 50 },
                        fontSize: { xs: "1rem", sm: "1rem" },
                        borderRadius: "20px !important",
                      }}
                    // className="toggle-button"
                    >
                      Join Appointment
                    </DeedButtons>

                    {
                      //   activeView === "join" && (
                      //     <Button
                      //     className="toggle-button"
                      //     // fullWidth
                      //     onClick={handleClickOnConsultationComplete}
                      //     // disabled={!endConsultationFn}
                      //     sx={{
                      //      margin: "0px 5px", width: "50%",
                      //      //  display: 'flex',
                      //      //  alignItems: 'center',
                      //      //  justifyContent: 'center',
                      //      //  padding: '12px',
                      //       backgroundColor: '#ffffff',
                      //       color: '#4caf50',
                      //       border: '1px solid rgb(79, 215, 86)',
                      //       borderRadius: '8px',
                      //       '&:hover': {
                      //         color: '#000000',
                      //         backgroundColor: '#43a047',
                      //       },
                      //       '&.Mui-disabled': {
                      //         backgroundColor: '#e0e0e0',
                      //         color: '#9e9e9e',
                      //         border: '1px solid rgb(21, 201, 72)',
                      //       },
                      //      //  margin: '20px auto',
                      //     }
                      //   }
                      //   >
                      //     <CallEndRounded sx={{ mr: 1 }} />
                      //     {/* <Typography
                      //       variant="button"
                      //       sx={{
                      //         fontWeight: 500,
                      //         textTransform: 'none',
                      //         fontSize: '1rem'
                      //       }}
                      //     > */}
                      //       End Consultation
                      //     {/* </Typography> */}
                      //   </Button>
                      //  )
                    }

                  </>

                )}

                {/* <Button
                  sx={{
                    ":hover": {
                      backgroundColor: "#ff8139",
                      color: "#ffffff",
                    },
                    ...(isExpanded
                      ? { margin: "0px 5px", width: "50%" }
                      : { margin: "0px 5px" }),
                  }}
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleToggleChatView()}
                  className="toggle-button"
                >
                  Appointment Chat
                </Button> */}

                <Button
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 1,
                    borderRadius: "20px",
                    fontWeight: 700,
                    fontSize: "1rem",
                    boxShadow:
                      activeChatView === "chat"
                        ? "0 4px 16px 0 rgba(255,129,57,0.15)"
                        : "0 2px 8px 0 rgba(25,118,210,0.08)",
                    background:
                      activeChatView === "chat"
                        ? "linear-gradient(90deg, #ff8139 0%, #ffb347 100%)"
                        : "#fff",
                    color: activeChatView === "chat" ? "#fff" : "#ff8139",
                    "&:hover": {
                      background:
                        activeChatView === "chat"
                          ? "linear-gradient(90deg, #ff8139 0%, #ffb347 100%)"
                          : "#fff",
                      color: activeChatView === "chat" ? "#fff" : "#ffb347",
                      boxShadow: "0 6px 20px 0 rgba(255,129,57,0.18)",
                    },
                    ...(isExpanded
                      ? { margin: "0px 5px", width: "50%" }
                      : { margin: "0px 5px" }),
                  }}
                  variant={activeChatView === "chat" ? "contained" : "outlined"}
                  color="secondary"
                  onClick={handleToggleChatView}
                  className="toggle-button"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transition: "transform 0.3s",
                      // transform: activeChatView === "chat" ? "rotate(-15deg)" : "rotate(0deg)",
                    }}
                  >
                    {activeChatView === "chat" ? (
                      <ToggleOnOutlined />
                    ) : (
                      <ToggleOffOutlined />
                    )}
                  </Box>
                  <Typography fontWeight={700} fontSize="0.9rem">
                    {activeChatView === "chat"
                      ? "Hide Report View"
                      : "Show Report View"}
                  </Typography>
                </Button>
              </>
            )}
          </div>
        </Paper>
      </Box>

      <div className="dynamic-views">
        {activeView === "join" &&
          (secretData?.appointmentData?.appointmentId ? (
            <>
              <VideoCall
                appId="ca59f0007146426fb86e222f858f0cca"
                channelName={secretData?.appointmentData?.appointmentId}
                token={null}
                onCallEnd={handleEndVideoCall}
                appointmentData={secretData?.appointmentData}
                endConsultationClicked={endConsultationClicked}
              />
              {/* <Button
                // fullWidth
                onClick={handleClickOnConsultationComplete}
                // disabled={!endConsultationFn}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  backgroundColor: '#ffffff',
                  color: '#4caf50',
                  border: '1px solid rgb(79, 215, 86)',
                  borderRadius: '8px',
                  '&:hover': {
                    color: '#000000',
                    backgroundColor: '#43a047',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                    border: '1px solid rgb(21, 201, 72)',
                  },
                  margin: '20px auto',
                }}
              >
                <CallEndRounded sx={{ mr: 1 }} />
                <Typography
                  variant="button"
                  sx={{
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}
                >
                  Complete Consultation
                </Typography>
              </Button> */}
            </>
          ) : (
            ""
          ))}
        <div className="chat-container" ref={chatRef}>
          {activeChatView === "chat" && (
            <ChatComponent aptId={secretData?.appointmentData?.appointmentId} />
          )}
        </div>
      </div>
    </div>
  );
};

export { JoinVideoConsultation };
